<template>
    <div class="home" v-if="doneLoading && opts.attachmentTypeOptions && opts.facilityTypeOptions && opts.textingPreferenceOptions">
        <b-container fluid>
            <b-row>
                <b-col>
                    <h1> Interstate Endorsement Application Resubmission</h1>
                </b-col>
            </b-row>

            <div class="card">
                <div class="card-header">Personal Information</div>
                <div class="card-body">
                    <div  class="alert-danger p-2 mb-2" v-if="corrections.personalInfoCorrections.reasons || hasFlaggedPersonalInfoFields">
                        <div v-if="corrections.personalInfoCorrections.reasons">
                            <b-row>
                                <b-col class="font-weight-bold">
                                    MDH admin who rejected your application has provided the following reasons regarding your personal information:
                                </b-col>
                            </b-row>
                            <b-row>
                                <b-col>
                                    {{corrections.personalInfoCorrections.reasons}}
                                </b-col>
                            </b-row>
                        </div>
                        <b-row v-if="hasFlaggedPersonalInfoFields">
                            <b-col class="font-weight-bold">
                                Fields with issues have been highlighted in red.
                            </b-col>
                        </b-row>
                    </div>
                    <b-row>
                        <b-col cols="4">
                            <BaseTextOutput label="First Name" :value="profile.firstName"/>
                        </b-col>
                        <b-col cols="4">
                            <BaseTextOutput label="Middle Name" :value="profile.middleName"/>
                        </b-col>
                        <b-col cols="4">
                            <BaseTextOutput label="Last Name" :value="profile.lastName"/>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col cols="12" lg="6" v-bind:class="corrections.personalInfoCorrections.dob?'is-invalid':''">
                            <BaseDateOfBirthInput v-model="profile.dateOfBirth"
                                                  :v="$v.profile.dateOfBirth"
                                                  label="Date of Birth"
                                                  :messages="dobMessages"
                                                  slug="dob"
                                                  setId="dob"
                                                  @input="corrections.personalInfoCorrections.dob=false"
                                                  tip="Format: MM/DD/YYYY"
                            />
                        </b-col>
                        <b-col cols="12" lg="6" v-bind:class="corrections.personalInfoCorrections.ssn?'is-invalid':''">
                            <BaseMaskedTextInput v-model="profile.ssn"
                                                 :v="$v.profile.ssn"
                                                 slug="ssn"
                                                 label="Social Security Number"
                                                 setId="ssn"
                                                 mask="###-##-####"
                                                 @input="corrections.personalInfoCorrections.ssn=false"
                            />
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col cols="6" v-bind:class="corrections.personalInfoCorrections.phone?'is-invalid':''">
                            <BaseMaskedTextInput v-model="profile.contact.phone"
                                                 :v="$v.profile.contact.phone"
                                                 slug="phone"
                                                 label="Cell Phone Number"
                                                 setId="phone"
                                                 mask="(###)###-####"
                                                 @input="corrections.personalInfoCorrections.phone=false"
                            />
                        </b-col>
                        <b-col cols="6">
                            <BaseTextOutput label="Email" :value="profile.contact.email"/>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col>
                            <BaseSelector v-model="profile.textingPreferences"
                                          :v="$v.profile.textingPreferences"
                                          slug="textingPreference"
                                          label="Texting Preferences"
                                          :options="opts.textingPreferenceOptions"/>
                        </b-col>
                    </b-row>
                    <h2>
                        Address:
                    </h2>
                    <b-row>
                        <b-col cols="8" v-bind:class="corrections.personalInfoCorrections.address.streetAddress?'is-invalid':''">
                            <BaseTextInput v-model="profile.contact.address.streetAddress"
                                           :v="$v.profile.contact.address.streetAddress"
                                           slug="streetAddress"
                                           label="Street Address"
                                           tip="Please include apartment number or PO Box number if relevant."
                                           setId="persStreet"
                                           @input="corrections.personalInfoCorrections.address.streetAddress=false"
                            />
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col cols="4" v-bind:class="corrections.personalInfoCorrections.address.city?'is-invalid':''">
                            <BaseTextInput v-model="profile.contact.address.city"
                                           :v="$v.profile.contact.address.city"
                                           slug="city"
                                           label="City"
                                           setId="persCity"
                                           @input="corrections.personalInfoCorrections.address.city=false"
                            />
                        </b-col>
                        <b-col cols="4" v-bind:class="corrections.personalInfoCorrections.address.state?'is-invalid':''">
                            <BaseSelector v-model="profile.contact.address.state"
                                          :v="$v.profile.contact.address.state"
                                          label="State"
                                          slug="state"
                                          :options=opts.stateOptions
                                          setId="persState"
                                          @input="firstPersonalStateUpdateCatch?corrections.personalInfoCorrections.address.state=false:firstPersonalStateUpdateCatch=true"
                            />
                        </b-col>
                        <b-col cols="4" v-bind:class="corrections.personalInfoCorrections.address.postalCode?'is-invalid':''">
                            <BaseMaskedTextInput v-model="profile.contact.address.postalCode"
                                                 :v="$v.profile.contact.address.postalCode"
                                                 slug="postalCode"
                                                 label="ZIP"
                                                 mask="#####-####"
                                                 setId="persZip"
                                                 @input="corrections.personalInfoCorrections.address.postalCode=false"
                            />
                        </b-col>
                    </b-row>
                </div>
            </div>
            <div class="card">
                <div class="card-header">Interstate Endorsement Information</div>
                <div class="card-body">
                    <div class="alert alert-info" role="alert">
                        <b-row>
                            <b-col>
                                Please note: Your registration will be calculated on either your test results date or your most recent employment date,
                                whichever is later.
                            </b-col>
                        </b-row>
                    </div>
                    <div class="alert alert-danger" v-if="isDirty() && $v.application.endorsement.hasWorkOrTest.$invalid" role="alert">
                        <b-row>
                            <b-col>
                                You need to have tested within the past 24 months from the endorsed state OR been employed at a facility in the last 24
                                months in the state where you are currently endorsed to complete the interstate endorsement application.
                            </b-col>
                        </b-row>
                    </div>
                    <div class="alert-danger p-2 mb-2" v-if="corrections.certificationCorrections.reasons || hasFlaggedCertificationInfoFields">
                        <div v-if="corrections.certificationCorrections.reasons">
                            <b-row>
                                <b-col class="font-weight-bold">
                                    MDH admin who rejected your application has provided the following reasons regarding your certification:
                                </b-col>
                            </b-row>
                            <b-row>
                                <b-col>
                                    {{corrections.certificationCorrections.reasons}}
                                </b-col>
                            </b-row>
                        </div>
                        <b-row v-if="hasFlaggedCertificationInfoFields">
                            <b-col class="font-weight-bold">
                                Fields with issues have been highlighted in red.
                            </b-col>
                        </b-row>
                    </div>
                    <b-row>
                        <b-col cols="6" v-bind:class="corrections.certificationCorrections.state?'is-invalid':''">
                            <BaseSelector v-model="application.endorsement.state"
                                          :v="$v.application.endorsement.state"
                                          label="State"
                                          slug="state"
                                          :options=opts.stateOptions
                                          :tip="'Please indicate the state you are endorsing from.'"
                                          setId="endState"
                                          @input="firstEndorsementStateUpdateCatch?corrections.certificationCorrections.state=false:firstEndorsementStateUpdateCatch=true"
                            />
                        </b-col>
                        <b-col cols="6" v-bind:class="corrections.certificationCorrections.certificateNumber?'is-invalid':''">
                            <BaseTextInput v-model="application.endorsement.certificateNumber"
                                           :v="$v.application.endorsement.certificateNumber"
                                           slug="certificateNumber"
                                           label="State Certificate Number"
                                           setId="endCertNo"
                                           @input="corrections.certificationCorrections.certificateNumber=false"
                            />
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col cols="4" v-bind:class="corrections.certificationCorrections.workPeriod?'text-danger':''">
                            <BaseRadioInput v-model="application.endorsement.workPeriod"
                                            :v="$v.application.endorsement.workPeriod"
                                            slug="workPeriod"
                                            label="In the past 24 months, were you working at a facility in the state you are currently endorsing from?"
                                            :options=opts.yesNoBoolOptions
                                            setId="endWorkPeriod"
                                            @input="firstEndorsementWorkRadioUpdateCatch?corrections.certificationCorrections.workPeriod=false:firstEndorsementWorkRadioUpdateCatch=true"
                            />
                        </b-col>
                        <b-col cols="4" v-bind:class="corrections.certificationCorrections.testPeriod?'text-danger':''">
                            <BaseRadioInput v-model="application.endorsement.testPeriod"
                                            :v="$v.application.endorsement.testPeriod"
                                            slug="testPeriod"
                                            label="Did you test within the past 24 months in the state you are endorsed by?"
                                            :options=opts.yesNoBoolOptions
                                            setId="endTestPeriod"
                                            @input="firstEndorsementTestRadioUpdateCatch?corrections.certificationCorrections.testPeriod=false:firstEndorsementTestRadioUpdateCatch=true"
                                            />
                        </b-col>
                        <b-col cols="4" v-bind:class="corrections.certificationCorrections.testDate?'text-danger':''"
                               v-if="application.endorsement.testPeriod === true">
                            <BaseDateOfBirthInput v-model="application.endorsement.testDate"
                                           :v="$v.application.endorsement.testDate"
                                           slug="testDate"
                                           label="Date Tested in Endorsing State"
                                           setId="endTestDate"
                                           @input="corrections.certificationCorrections.testDate=false"
                                           tip="Format: MM/DD/YYYY"
                            />
                        </b-col>
                    </b-row>
                    <div v-if="corrections.facilityCorrections.reasons" class="alert-danger mb-2">
                        <b-row>
                            <b-col class="font-weight-bold">
                                MDH admin who rejected your application has provided the following reasons regarding your facility:
                            </b-col>
                        </b-row>
                        <b-row>
                            <b-col>
                                {{corrections.facilityCorrections.reasons}}
                            </b-col>
                        </b-row>
                    </div>
                    <b-row v-if="hasFlaggedFacilityInfoFields">
                        <b-col>
                            Fields with issues have been highlighted in red.
                        </b-col>
                    </b-row>
                    <div v-if="application.endorsement.workPeriod">
                        <b-row>
                            <b-col cols="6" v-bind:class="corrections.facilityCorrections.name?'is-invalid text-danger':''">
                                <BaseTextInput v-model="application.facility.update.name"
                                               :v="$v.application.facility.update.name"
                                               slug="facilityName"
                                               label="Facility Name"
                                               @input="corrections.facilityCorrections.name=false"
                                />
                            </b-col>
                            <b-col cols="6" class="pt-4" v-bind:class="corrections.facilityCorrections.staffingAgency?'text-danger':''">
                                <BaseCheckboxInput label="I am working at this facility through a staffing agency."
                                                   v-model="application.facility.update.staffingAgency"
                                                   :v="$v.application.facility.update.staffingAgency"
                                                   slug="staffingAgency"
                                                   setId="staffingAgency"
                                                   @input="firstFacilityStaffingAgencyCheckboxUpdateCatch?corrections.facilityCorrections.staffingAgency=false:firstFacilityStaffingAgencyCheckboxUpdateCatch=true"
                                />
                            </b-col>
                        </b-row>
                        <h3>
                            Facility Address:
                        </h3>
                        <b-row>
                            <b-col cols="8" v-bind:class="corrections.facilityCorrections.address.streetAddress?'is-invalid':''">
                                <BaseTextInput v-model="application.facility.update.address.streetAddress"
                                               :v="$v.application.facility.update.address.streetAddress"
                                               slug="facilityStreetAddress"
                                               label="Street Address"
                                               tip="Please include apartment number or PO Box number if relevant."
                                               setId="facAddress"
                                               @input="corrections.facilityCorrections.address.streetAddress=false"
                                />
                            </b-col>
                        </b-row>
                        <b-row>
                            <b-col cols="4" v-bind:class="corrections.facilityCorrections.address.city?'is-invalid':''">
                                <BaseTextInput v-model="application.facility.update.address.city"
                                               :v="$v.application.facility.update.address.city"
                                               slug="city"
                                               label="City"
                                               setId="facCity"
                                               @input="corrections.facilityCorrections.address.city=false"
                                />
                            </b-col>
                            <b-col cols="4" v-bind:class="corrections.facilityCorrections.address.state?'is-invalid':''">
                                <BaseSelector v-model="application.facility.update.address.state"
                                              :v="$v.application.facility.update.address.state"
                                              label="State"
                                              slug="state"
                                              setId="facState"
                                              :options=opts.stateOptions
                                              @input="firstFacilityStateUpdateCatch?corrections.facilityCorrections.address.state=false:firstFacilityStateUpdateCatch=true"
                                />
                            </b-col>
                            <b-col cols="4" v-bind:class="corrections.facilityCorrections.address.postalCode?'is-invalid':''">
                                <BaseMaskedTextInput v-model="application.facility.update.address.postalCode"
                                                     :v="$v.application.facility.update.address.postalCode"
                                                     slug="postalCode"
                                                     label="ZIP"
                                                     mask="#####-####"
                                                     setId="facZip"
                                                     @input="corrections.facilityCorrections.address.postalCode=false"
                                />
                            </b-col>
                        </b-row>
                    </div>
                </div>
            </div>
            <div class="card">
                <div class="card-header">Attachments</div>
                <div class="card-body">

                    <div v-if="corrections.attachmentCorrectionReasons" class="alert-danger mb-2">
                        <b-row>
                            <b-col class="font-weight-bold">
                                MDH admin who rejected your application has provided the following reasons regarding your attachments:
                            </b-col>
                        </b-row>
                        <b-row>
                            <b-col>
                                {{corrections.attachmentCorrectionReasons}}
                            </b-col>
                        </b-row>
                    </div>
                    <b-row v-if="hasFlaggedAttachmentInfoFields">
                        <b-col>
                            Attachments with issues have been highlighted in red.
                        </b-col>
                    </b-row>
                    
                    <b-row v-bind:class="attachmentIssues.get('CERT')?'text-danger':''">
                        <b-col cols="3">
                            <BaseTextOutput label="File name" :value="getAttachmentName('CERT')"/>
                            <span v-if="!hasAttachment('CERT')" span class="green-required align-top"/>
                        </b-col>
                        <b-col cols="3">
                            <BaseTextOutput label="Type" :value="getAttachmentTypeDescription('CERT')"/>
                        </b-col>
                        <b-button variant="secondary" class="m-2" :disabled="!hasAttachment('CERT')"  @click="downloadAttachment('CERT')">Download</b-button>
                    </b-row>
                    <b-row>
                        <b-col>
                            <div v-if="missingAttachment('CERT')" class="d-block invalid-feedback">Nurse Aide Certificate is required.</div>
                        </b-col>
                    </b-row>
                    <b-row v-bind:class="attachmentIssues.get('SSC')?'text-danger':''">
                        <b-col cols="3">
                            <BaseTextOutput label="File name" :value="getAttachmentName('SSC')"/>
                            <span v-if="!hasAttachment('SSC')" span class="green-required align-top"/>
                        </b-col>
                        <b-col cols="3">
                            <BaseTextOutput label="Type" :value="getAttachmentTypeDescription('SSC')"/>
                        </b-col>
                        <b-button variant="secondary" class="m-2"  :disabled="!hasAttachment('SSC')"  @click="downloadAttachment('SSC')">Download</b-button>
                    </b-row>
                    <div v-if="missingAttachment('SSC')" class="d-block invalid-feedback">Social Security Card is required.</div>
                    <div v-if="application.endorsement.testPeriod">
                         <b-row v-bind:class="attachmentIssues.get('IET')?'text-danger':''">
                            <b-col cols="3">
                                <BaseTextOutput label="File name" :value="getAttachmentName('IET')"/>
                                <span v-if="!hasAttachment('IET')" span class="green-required align-top"/>
                            </b-col>
                            <b-col cols="3">
                                <BaseTextOutput label="Type" :value="getAttachmentTypeDescription('IET')"/>
                            </b-col>
                            <b-button variant="secondary" class="m-2"  :disabled="!hasAttachment('IET')"  @click="downloadAttachment('IET')">Download</b-button>
                         </b-row>
                        <b-row v-if="missingAttachment('IET')">
                            <b-col>
                                <div class="d-block invalid-feedback">Transferring State Testing Results are required.</div>
                            </b-col>
                        </b-row>
                    </div>
                    <div v-if="application.endorsement.workPeriod">
                        <b-row v-bind:class="attachmentIssues.get('PS')?'text-danger':''">
                            <b-col cols="3">
                                <BaseTextOutput label="File name" :value="getAttachmentName('PS')"/>
                                <span v-if="!hasAttachment('PS')" span class="green-required align-top"/>
                            </b-col>
                            <b-col cols="3">
                                <BaseTextOutput label="Type" :value="getAttachmentTypeDescription('PS')"/>
                            </b-col>
                            <b-button variant="secondary" class="m-2"  :disabled="!hasAttachment('PS')"  @click="downloadAttachment('PS')">Download</b-button>
                        </b-row>
                        <b-row v-if="missingAttachment('PS')">
                            <b-col>
                                <div class="d-block invalid-feedback">Paystub is required.</div>
                            </b-col>
                        </b-row>
                    </div>
                    <div v-if="application.endorsement.workPeriod && !application.endorsement.testPeriod">
                         <b-row v-bind:class="attachmentIssues.get('JD')?'text-danger':''">
                            <b-col cols="3">
                                <BaseTextOutput label="File name" :value="getAttachmentName('JD')" />
                                <span v-if="!hasAttachment('JD')" span class="green-required align-top"/>
                            </b-col>
                            <b-col cols="3">
                                <BaseTextOutput label="Type"  :value="getAttachmentTypeDescription('JD')"/>
                            </b-col>
                            <b-button variant="secondary" class="m-2"  :disabled="!hasAttachment('JD')" @click="downloadAttachment('JD')">Download</b-button>
                         </b-row>
                         <b-row v-if="missingAttachment('JD')">
                             <b-col>
                                 <div class="d-block invalid-feedback">Job Description is required.</div>
                             </b-col>
                         </b-row>
                    </div>

                    <b-row>
                        <FileAttachment
                                v-on:attachment-added="addAttachment"
                                :attachmentTypeOptions="trimAttachmentTypeOptions()"
                                describedBy="attachments"/>
                    </b-row>
                </div>
            </div>

            <div class="card" v-if="application.external">
                <div class="card-header">Messages to/from MDH</div>
                <div class="card-body">
                    <b-row class="mt-2">
                        <b-col cols="12">
                            <b-table hover small show-empty class="sr-caption"
                                     aria-live="polite"
                                     responsive="true"
                                     :fields="noteFields"
                                     :items="application.external"
                                     :busy="false"
                                     label-sort-asc=""
                                     label-sort-desc=""
                                     :tbody-tr-class="highlightUnread"
                                     caption="Messages sent to/from MDH table.  To sort the data in this table by a specific column,
                                go to the appropriate column header cell and use enter to sort the data ascending.
                                Use enter again to sort descending.">
                                <template v-slot:no-data>
                                    No Messages Found.
                                </template>
                                <div slot="table-busy" class="text-center text-danger my-2">
                                    <b-spinner class="align-middle" label="Loading, Please Wait" aria-label="Loading, Please Wait"></b-spinner>
                                    <strong>Loading...</strong>
                                </div>
                                <template slot="empty" slot-scope="scope">
                                    No Messages Found.
                                </template>
                                <template v-slot:cell(noteType)="row">
                                    <div v-if="row.item && row.item.noteType.code==='EXT'">From MDH</div>
                                    <div v-else-if="row.item && row.item.noteType.code==='EXTIN'">To MDH</div>
                                    <div v-else>{{row.item.noteType.description}}</div>
                                </template>
                                <template v-slot:cell(noteDate)="row">
                                    {{formatNoteDateTime(row.item.noteDate)}}
                                </template>
                                <template v-slot:cell(noteByName)="row">
                                    <div v-if="row.item && row.item.noteType.code==='EXT'">MDH Staff</div>
                                    <div v-else>{{row.item.noteByName}}</div>
                                </template>
                            </b-table>
                        </b-col>
                    </b-row>
                </div>
            </div>
            <b-row>
                <b-col>
                    <BaseParagraphInput v-model="comments" :v="$v.comments" label="Additional Comments" tip="If you wish to explain anything regarding your application you can do so here. Your response is optional and will be attached to your application."/>
                </b-col>
            </b-row>

            <div class="card">
                <div class="card-header">Applicant Affirmation</div>
                <div class="card-body">
                    <b-row>
                        <b-col>
                          I certify the information provided in this application is true and accurate to the best of my knowledge.
                          I have read and will comply with the requirements of the <a href="https://www.revisor.mn.gov/statutes/cite/144A.61" target="_blank">Minnesota Statutes, section 144A.61</a>, and the Code of Federal Regulations, Title 42, Part 483, <a href="https://www.ecfr.gov/current/title-42/section-483.35" target="_blank">Subpart B, section 438.35</a>, and <a href="https://www.ecfr.gov/current/title-42/section-483.156" target="_blank">Subpart D, section 483.156</a>.
                          I understand that knowingly making a false statement on this application could be cause for denial, suspension, or revocation of registration.
                          I understand by signing this document, I give MDH the authority to contact any listed supervisor, employer, and/or client submitted for use in verification of credentials and employment, as well as any other relevant information that determines what is necessary to properly evaluate an applicant for registration.
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col cols="12" lg="6">
                            <BaseTextInput v-model="signature"
                                           :v="$v.signature"
                                           slug="signature"
                                           label="Signature"
                                           setId="sig"/>
                        </b-col>
                        <b-col cols="12" lg="6">
                            <BaseDateInput v-model="dateSigned"
                                           :v="$v.dateSigned"
                                           label="Date"
                                           slug="dateSigned"
                                           :disabled="true"
                                           setId="dtSig"
                                           tip="Format: MM/DD/YYYY"
                            />
                        </b-col>
                    </b-row>
                </div>
            </div>

            <b-row>
                <b-col>
                    <b-button variant="primary" :disabled="isDisabled" @click="submit">
                        Resubmit Application
                    </b-button>
                </b-col>
                <b-col>
                    <b-button variant="secondary" @click="returnToView">
                        Cancel
                    </b-button>
                </b-col>
            </b-row>
        </b-container>
    </div>
</template>

<script>
    import axios from 'axios';
    import axiosRetry from 'axios-retry';
    import BaseTextInput from "@/components/templates/input/BaseTextInput";
    import BaseSelector from "@/components/templates/input/BaseSelector";
    import BaseTextOutput from "@/components/templates/input/BaseTextOutput";
    import BaseMaskedTextInput from "@/components/templates/input/BaseMaskedTextInput";
    import { required, requiredIf, minLength, maxLength } from 'vuelidate/lib/validators';
    import {commonOptions} from "@/mixins/commonOptions";
    import BaseCheckboxInput from "@/components/templates/input/BaseCheckboxInput";
    import minDate from "@/validators/minDate";
    import maxDate from "@/validators/maxDate";
    import BaseDateInput from "@/components/templates/input/BaseDateInput";
    import BaseParagraphInput from "@/components/templates/input/BaseParagraphInput";
    import moment from 'moment';
    import BaseRadioInput from "@/components/templates/input/BaseRadioInput";
    import FileAttachment from "@/components/templates/input/FileAttachment";

    axiosRetry(axios, { retries: 0,
        retryDelay: axiosRetry.exponentialDelay  })

    const correctAttachments = (obj, parent) => (obj.find(a=>a.type.code==="CERT")!=null) && (obj.find(a=>a.type.code==="SSC")!=null) && (!parent.endorsement.testPeriod || obj.find(a=>a.type.code==="IET")!=null) && (!parent.endorsement.workPeriod || obj.find(a=>a.type.code==="PS")!=null) && (!(parent.endorsement.workPeriod && !parent.endorsement.testPeriod) || obj.find(a=>a.type.code==="JD")!=null);

    export default {
        name: "ResubmitRenewal",
        components:{
            BaseTextInput,
            BaseTextOutput,
            BaseSelector,
            BaseMaskedTextInput,
            BaseCheckboxInput,
            BaseDateInput,
            BaseParagraphInput,
            BaseRadioInput,
            FileAttachment,
        },
        data:()=>({
            isDisabled: false,
            doneLoading: false,
            dobMessages: {
                maxDate: 'Date of Birth cannot be in the future.',
                minDate: 'Date of Birth must be after 01/01/1900.'
            },
            opts: {
                facilityTypeOptions: [],
                attachmentTypeOptions: [],
                textingPreferenceOptions: [],
            },
            application: {},
            facility: {
                update: {
                    name: null,
                    address:{
                        streetAddress: null,
                        city: null,
                        state: null,
                        postalCode: null
                    },
                    staffingAgency: null
                }
            },
            corrections: null,
            profile: null,
            comments: '',
            firstPersonalStateUpdateCatch: false,
            firstEndorsementStateUpdateCatch: false,
            firstEndorsementTestRadioUpdateCatch: false,
            firstEndorsementWorkRadioUpdateCatch: false,
            firstFacilityStaffingAgencyCheckboxUpdateCatch: false,
            firstFacilityStateUpdateCatch: false,
            hasFlaggedFacilityInfoFields: false,
            hasFlaggedPersonalInfoFields: false,
            hasFlaggedAttachmentInfoFields: false,
            hasFlaggedCertificationInfoFields: false,
            showFlaggedFieldsError: false,
            noteFields: [
                {key: 'note', label: 'Message',sortable:true},
                {key: 'noteType',label:'Message Type', sortable:true},
                {key: 'noteDate',label:'Message Date',sortable:true},
                {key:'noteByName',sortable:true,label:'Message By'},
            ],
            signature: null,
            dateSigned: new moment().format('YYYY-MM-DD'),
            attachmentIssues: new Map([
                ['CERT', false],
                ['PS', false],
                ['SSC', false],
                ['IET', false],
                ['JD', false]
            ]),
        }),
        created() {
            this.loadCorrections();
            this.loadFacilityTypeOptions();
            this.loadAttachmentTypeOptions();
            this.loadTextingOptions();
        },
        methods:{
            loadApplication() {
                axios.get(`${process.env.VUE_APP_API_BASE_URL}/application/renewal/${this.$route.params.id}`)
                    .then(response => {
                        this.application = response.data.application;
                        if (this.application.employers[0]) {
                            this.application.facility = this.facility;
                            this.application.facility.update = Object.assign({}, this.application.employers[0]);
                        } else {
                            this.application.facility = this.facility;
                            this.application.facility.update  = Object.assign({}, this.facility.update);
                        }
                        if(this.application.facility.update.staffingAgency == null){
                            this.application.facility.update.staffingAgency=false;
                        }
                        delete this.application.facility.update.facilityType;
                        delete this.application.employers;
                        this.profile = response.data.profile;
                        if (this.profile.textingPreferences) {
                            this.profile.textingPreferences = this.profile.textingPreferences.code
                        }
                        this.$nextTick(()=>{
                            this.matchCorrectionsToAttachment();
                        });
                    })
            },
            loadCorrections(){
                axiosRetry(axios, {retries: 0});
                axios.get(`${process.env.VUE_APP_API_BASE_URL}/application/corrections/${this.$route.params.id}`)
                    .then(response=>{
                        this.corrections = response.data;
                    })
                    .catch(error=> {
                        this.corrections = {
                            personalInfoCorrections: {
                                address: {
                                    streetAddress:false,
                                    city:false,
                                    state:false,
                                    postalCode:false
                                },
                                phone:false,
                                dob:false,
                                ssn:false,
                                reasons:''
                            },
                            facilityCorrections: {
                                name:false,
                                facilityType:false,
                                department:false,
                                staffingAgency:false,
                                address: {
                                    streetAddress:false,
                                    city:false,
                                    state:false,
                                    postalCode:false,
                                },
                                reasons:''
                            },
                            certificationCorrections: {
                                state:false,
                                certificateNumber:false,
                                testPeriod:false,
                                testDate:false,
                                workPeriod:false,
                                reasons: ''
                            },
                            attachmentCorrections: []

                        }
                    })
                    .finally(()=>{
                        this.$nextTick(()=>{
                            this.loadApplication();
                        });
                    })
            },
            loadFacilityTypeOptions(){
                axios.get(`${process.env.VUE_APP_API_BASE_URL}/application/facilityTypes`, {ignoreHeader: true})
                    .then(response => {
                        for(let type of response.data){
                            this.opts.facilityTypeOptions.push({
                                value: type.code,
                                text: type.description
                            })
                        }
                    })
            },
            loadAttachmentTypeOptions(){
                axios.get(`${process.env.VUE_APP_API_BASE_URL}/attachments/public/interstateTypes/all`)
                    .then(response => {
                        for(let type of response.data){
                            this.opts.attachmentTypeOptions.push({
                                value: type.code,
                                text: type.description
                            })
                        }
                    });
            },
            loadTextingOptions(){
                axios.get(`${process.env.VUE_APP_API_BASE_URL}/profile/texting/getList`)
                    .then(response=>{
                        for(let option of response.data){
                            this.opts.textingPreferenceOptions.push({
                                value: option.code,
                                text: option.description
                            })
                        }
                    })
            },
            matchCorrectionsToAttachment(){
                let temp = [];
                for(let i=0;i<this.application.attachments.length;i++){
                    let correction = this.corrections.attachmentCorrections.find(x=>x.id===this.application.attachments[i].attachmentId);
                    if(correction){
                        temp.push(correction);
                        this.attachmentIssues.set(this.application.attachments[i].type.code, correction.mark);
                    }
                    else{
                        temp.push({
                            id: this.application.attachments[i].attachmentId,
                            mark: false
                        })
                    }
                    let app = this.application.attachments[i]
                    if(app.grade) {
                        app.grade = app.grade.code
                    }
                }
                this.corrections.attachmentCorrections = temp;
                this.$nextTick(()=>{
                    this.hasFlaggedFacilityInfoFields = this.calcFlaggedFields(this.corrections.facilityCorrections);
                    this.hasFlaggedPersonalInfoFields = this.calcFlaggedFields(this.corrections.personalInfoCorrections);
                    this.hasFlaggedCertificationInfoFields = this.calcFlaggedFields(this.corrections.certificationCorrections);
                    this.hasFlaggedAttachmentInfoFields = this.calcFlaggedAttachmentFields();
                    this.$nextTick(()=>{
                        this.doneLoading=true;

                    })
                })
            },
            findAttachmentFileName(employer, type){
                let file = employer.attachments.find(x=>x.type.code === type);
                if(file){
                    return file.filename;
                }
                return "N/A"
            },
            downloadAttachment(type){
                let id = this.application.attachments.find(x=>x.type.code===type).attachmentId;
                axios.get(`${process.env.VUE_APP_API_BASE_URL}/attachments/getUrl/${id}`)
                    .then(response=>{
                        if(response.data){
                            window.open(response.data,"_blank");
                        }
                        else{this.$emit('attachment-get-failure')}
                    })
            },
            hasAttachmentType(employer, type){
                return employer.attachments.find(x=>x.type.code===type);
            },
            addAttachment(value){
                let oldAttachment= this.application.attachments.find(x=>x.type.code===value.type.code);
                let oldAttachmentIndex=this.application.attachments.indexOf(oldAttachment);
                this.$delete(this.application.attachments, oldAttachmentIndex);
                this.$delete(this.corrections.attachmentCorrections, oldAttachmentIndex);
                this.application.attachments.push(value);
                this.corrections.attachmentCorrections.push({id: value.attachmentId, mark: false});
                this.attachmentIssues.set(value.type.code, false);
            },
            removeAttachment(employer, type){
                employer.attachments = employer.attachments.filter(x=>x.type.code!==type);
            },
            trimAttachmentTypeOptions(){
                let result = [];
                result.push(this.opts.attachmentTypeOptions.find(x=>x.value==='CERT'));
                result.push(this.opts.attachmentTypeOptions.find(x=>x.value==='SSC'));
                if(this.application.endorsement.testPeriod){
                    result.push(this.opts.attachmentTypeOptions.find(x=>x.value==='IET'));
                }
                if(this.application.endorsement.workPeriod){
                    result.push(this.opts.attachmentTypeOptions.find(x=>x.value==='PS'));
                }
                if(this.application.endorsement.workPeriod && !this.application.endorsement.testPeriod){
                    result.push(this.opts.attachmentTypeOptions.find(x=>x.value==='JD'));
                }
                return result;
            },
            showRootToast(title, message, type) {
                this.$root.$bvToast.toast(message, {
                    title: title,
                    toaster: 'b-toaster-top-center',
                    solid: true,
                    variant: type,
                    appendToast: true,
                    noAutoHide:true
                })
                this.toastFocus()
            },
            toastFocus() {
                setTimeout(() => {
                    let elem = document.getElementsByClassName("toast")
                    elem = elem[elem.length-1]
                    elem.focus()
                }, 100)
            },
            calcFlaggedAttachmentFields(){
                for(let i=0;i<this.corrections.attachmentCorrections.length;i++){
                    if(this.corrections.attachmentCorrections[i].mark){
                        return true;
                    }
                    return false;
                }
            },
            calcFlaggedFields(obj){
                let temp = this.flattenObject(obj);
                delete temp.reasons;
                let values = Object.values(temp);
                for(let i=0;i<values.length;i++){
                    if(values[i]){
                        return true;
                    }
                }
                return false;
            },

            flattenObject(obj){
                const flattened = {}
                Object.keys(obj).forEach((key) => {
                    const value = obj[key]
                    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                        Object.assign(flattened, this.flattenObject(value))
                    } else {
                        flattened[key] = value
                    }
                })

                return flattened
            },
            showNameModal() {
                this.$bvModal.show('name-modal')
            },
            closeNameModal() {
                this.$bvModal.hide('name-modal')
            },
            showSsnModal() {
                this.$bvModal.show('ssn-modal')
            },
            closeSsnModal() {
                this.$bvModal.hide('ssn-modal')
            },
            submit(){
                this.isDisabled = true;
                this.$v.$touch();
                if(this.$v.$invalid){
                    this.showRootToast('Error submitting application','Errors found in form','danger');
                    this.isDisabled=false;
                    return;
                }

                // if(this.checkAllFlaggedFields()){
                //     this.showRootToast('Error submitting application','Some marked fields haven\'t been corrected','danger');
                //     return;
                // }
                axios.post(`${process.env.VUE_APP_API_BASE_URL}/application/interstate/resubmit/${this.$route.params.id}`,
                    {
                        application: this.application,
                        profile: this.profile,
                        facility: this.application.facility.update,
                        attachments: this.filterAttachments(),
                        endorsement: this.application.endorsement

                    }).then(response => {
                            this.showRootToast('Application Resubmitted','Your application was been successfully resubmitted','success');
                            if(this.comments){
                                axios.post(`${process.env.VUE_APP_API_BASE_URL}/application/notes/${this.$route.params.id}/addFromUser`,this.comments)
                                    .catch(error=>{
                                        this.showRootToast('Error adding note','The comment you included with this application has failed to submit','danger');
                                    })
                            }
                            this.$nextTick(()=>{
                                this.$router.push({path: `/view-endorsement/${this.$route.params.id}`})
                            })
                        }).catch(error=>{
                    if(error.response.status===406){
                        if(error.response.data==='NoEdits'){
                            this.showRootToast('No edits applied','Application correction has been rejected because you have not changed anything. If you entered this page in error, please click on the cancel button on the bottom of the page to return to your application view.','warning');
                        }
                        else if(error.response.data==='InvalidStatus'){
                            this.showRootToast('Error submitting application','This application has received a final decision and is not eligible for corrections. Please submit a new interstate application.','danger');
                        }
                        else{
                            this.showRootToast('Error submitting application', 'The Application has failed to submit', 'danger');
                        }
                    }
                    else if(error.response.status===401){
                        this.showRootToast('Error submitting application','Your login into NAR has expired. Please refresh this page, log in again, and resubmit your corrections.','danger');
                    }
                    else if(error.response.status===500){
                        this.showRootToast('Error submitting application','The Application has failed to submit due to a server error. Please contact support at health.fpc-nar@state.mn.us.','danger');
                    }
                    else if(error.response.status===404){
                        this.showRootToast('Error submitting application', 'The Application not found.', 'danger');
                    }
                    else if(error.response.status===403){
                        this.showRootToast('Error submitting application', 'This application does not belong to you, please log in as the correct user and try again, or please press on Home button on top of the page to return to your dashboard.', 'danger');
                    }
                    else {
                        this.showRootToast('Error submitting application', 'The Application has failed to submit', 'danger');
                    }
                  this.isDisabled = false;
                })

            },

            checkAllFlaggedFields(){
                let flattened = [];

                let temp = this.flattenObject(this.corrections.personalInfoCorrections);
                delete temp.reasons;
                flattened = flattened.concat(Object.values(temp));

                temp = this.flattenObject(this.corrections.facilityCorrections);
                delete temp.reasons;
                flattened = flattened.concat(Object.values(temp));

                temp = this.flattenObject(this.corrections.certificationCorrections);
                delete temp.reasons;
                flattened = flattened.concat(Object.values(temp));

                flattened.push(this.attachmentIssues.get('CERT'));
                flattened.push(this.attachmentIssues.get('SSC'));
                if(this.application.endorsement.testPeriod){
                    flattened.push(this.attachmentIssues.get('IET'));
                }
                if(this.application.endorsement.workPeriod){
                    flattened.push(this.attachmentIssues.get('PS'))
                }
                if(this.application.endorsement.workPeriod && !this.application.endorsement.testPeriod){
                    flattened.push(this.attachmentIssues.get('JD'));
                }

                for(let i=0;i<flattened.length;i++){
                    if(flattened[i]){
                        return true;
                    }
                }
                return false;
            },

            filterAttachments(){
                let filtered = this.application.attachments;
                if(!(this.application.endorsement.workPeriod && !this.application.endorsement.testPeriod)){
                    filtered = filtered.filter(x=>x.type.code!=='JD');
                }
                if(!this.application.endorsement.workPeriod){
                    filtered = filtered.filter(x=>x.type.code!=='PS');
                }
                if(!this.application.endorsement.testPeriod){
                    filtered = filtered.filter(x=>x.type.code!=='IET');
                }
                return filtered;
            },

            missingAttachment(attachmentType){
                return this.$v.application.attachments.$dirty && this.$v.application.attachments.$invalid && this.application.attachments.find(a=>a.type.code===attachmentType)==null
            },
            highlightUnread(item,type){
                return (item != null && item.noteType.code==='EXT' && item.noteRead.code==="UNREAD") ? 'table-warning' : '';
            },
            formatNoteDateTime(noteDateTime){
                return moment(noteDateTime).format('MM/DD/YYYY hh:mmA');
            },
            returnToView(){
                this.$router.push({path: `/view-endorsement/${this.$route.params.id}`})
            },
            isDirty() {
                return this.$v.application.endorsement.testPeriod.$dirty && this.$v.application.endorsement.workPeriod.$dirty
            },
            getAttachmentName(type){
                let attachment = this.application.attachments.find(x=>x.type.code === type);
                return attachment?attachment.filename:'N/A'
            },
            getAttachmentTypeDescription(type){
                return this.opts.attachmentTypeOptions.find(x=>x.value===type).text;
            },
            hasAttachment(type){
                let attachment = this.application.attachments.find(x=>x.type.code === type);
                return !!attachment;
            },
            hasWorkPeriod() {
                return this.application.endorsement.workPeriod != null && this.application.endorsement.workPeriod === true
            }
            // attachmentHasIssues(type){
            //     let attachment = this.application.attachments.find(x=>x.type.code===type);
            //     if(attachment){
            //         return this.corrections.attachmentCorrections.find(x=>x.id === attachment.attachmentId).mark;
            //     }
            //     return false;
            // }
        },
        validations(){
            return{
                profile: {
                    ssn: {
                        required: required,
                        minLength: minLength(11),
                        maxLength: maxLength(11)
                    },
                    dateOfBirth: {
                        required,
                        maxDate: maxDate(new Date()),
                        minDate: minDate(new Date('1900-01-01'))
                    },
                    contact: {
                        required: true,
                        phone: {required},
                        address:{
                            required: true,
                            streetAddress: { required },
                            city: { required },
                            state: { required },
                            postalCode: {
                                required,
                                minLength: minLength(5),
                                maxLength: maxLength(10)
                            },
                        }
                    },
                    textingPreferences: {required}
                },
                application:{
                    required,
                    endorsement: {
                        hasWorkOrTest: {
                            reqWorkOrTest() {
                                return (this.application.endorsement.testPeriod != null && this.application.endorsement.workPeriod != null)
                                    && (this.application.endorsement.testPeriod || this.application.endorsement.workPeriod)
                            }
                        },
                        testPeriod: {required},
                        testDate: {
                            required: requiredIf(function () {
                                return this.application.endorsement.testPeriod === true
                            })
                        },
                        workPeriod: {required},
                        state: {required},
                        certificateNumber: {required},
                    },
                    facility: {
                        update: {
                            required,
                            staffingAgency: { required:false },
                            name: { required: requiredIf(function() { return this.hasWorkPeriod() }) },
                            address:{
                                required: true,
                                streetAddress: { required: requiredIf(function() { return this.hasWorkPeriod() }) },
                                city: { required: requiredIf(function() { return this.hasWorkPeriod() }) },
                                state: { required: requiredIf(function() { return this.hasWorkPeriod() }) },
                                postalCode: {
                                    required: requiredIf(function() { return this.hasWorkPeriod() }),
                                    minLength: minLength(5),
                                    maxLength: maxLength(10)
                                }
                            }
                        }
                    },
                    attachments: {
                        correctAttachments
                    }
                },
                comments: {required: false},
                signature: {required},
                dateSigned: {required}
            }
        },
        mixins: [
            commonOptions
        ]
    }
</script>

<style scoped>

</style>